import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import GearIcon from '@rsuite/icons/Gear' // Add GearIcon import

import { SectionTitle } from '../../ui_reusable/styles/SectionTitle.styles'
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import VersatileGrid from '../../ui_reusable/VersatileGrid'
import { TagSuccess, TagWarning } from '../../../utils/tag' // Remove TagDefault
import ProjectForm from './project_list/ProjectForm'
import {
  GridCellBoldText,
  GridCellNormalText,
  GridCellSmallFadedText,
} from '../../ui_reusable/styles/VersatileGridUtils.styles'
import { VerySmallNormalTag, SmallNormalTag } from '../../ui_reusable/styles/Tags.styles'
import { SmallStandardButton } from '../../ui_reusable/styles/Button.styles'
import { ThickSelectPicker } from '../../ui_reusable/StandardSelectPickers'
import { HeaderContainer } from '../../ui_reusable/styles/PlacementMisc.styles'
import { StatsContainer } from '../../ui_reusable/styles/VersatileGridUtils.styles'
import { HeaderMedium600Style } from '../../../styles/Text.styles'

import { useClientContext } from '../../../context/ClientContext'
import { useProjectContext } from '../../../context/ProjectContext'

const PAGE_SIZE = 8

const ProjectListPage = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [projects, setProjects] = useState([])
  const [total, setTotal] = useState(0)
  const [active, setActive] = useState(0)
  const [inactive, setInactive] = useState(0)
  const [page, setPage] = useState(1) // 1-based, matches `?page=` API contract
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(null) // { key (backend field), direction } | null
  const [projectOptions, setProjectOptions] = useState([])
  const [refresh, setRefresh] = useState(new Date().getTime())
  const { setSelectedClient } = useClientContext()
  const { setSelectedProject } = useProjectContext()

  // Flatten the project data for search functionality
  const flattenProjectData = (projects) => {
    return projects.map((project) => ({
      ...project,
      client: project.client?.name || '',
      kam: project.kam?.name || 'N/A',
      solutions: project.solutions.map((solution) => solution.name) || [],
    }))
  }

  // Fetch one page of projects (server-side pagination/filtering/sorting)
  useEffect(() => {
    const abortController = new AbortController()

    const getProjects = async () => {
      setLoading(true)
      try {
        const params = new URLSearchParams({
          limit: String(PAGE_SIZE),
          page: String(page),
        })
        if (search) {
          params.set('search', search)
        }
        if (sort) {
          params.set(
            'ordering',
            `${sort.direction === 'desc' ? '-' : ''}${sort.key}`,
          )
        }
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/projects/?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )

        // DRF 404s on out-of-range pages — step back and let the effect refire.
        if (response.status === 404 && page > 1) {
          setPage(page - 1)
          return
        }

        const res = await response.json()

        setProjects(res.data || [])
        setTotal(res.total ?? res.data?.length ?? 0)
        setActive(res.meta?.active ?? 0)
        setInactive(res.meta?.inactive ?? 0)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch projects:', error)
          setProjects([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    getProjects()

    return () => {
      abortController.abort()
    }
  }, [page, search, sort, refresh])

  useEffect(() => {
    const abortController = new AbortController()

    const getProjectOptions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/projects/?ordering=name`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )
        const res = await response.json()

        setProjectOptions(res.data || [])
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch project options:', error)
          setProjectOptions([])
        }
      }
    }

    getProjectOptions()

    return () => {
      abortController.abort()
    }
  }, [refresh])

  const handleManage = async (projectId) => {
    if (!projectId) return
    let project =
      projectOptions.find((elem) => elem.id === projectId) ||
      projects.find((elem) => elem.id === projectId)

    if (!project) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/projects/${projectId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          },
        )
        const res = await response.json()

        project = res.data
      } catch (error) {
        console.error('Failed to fetch project:', error)
        return
      }
    }

    if (!project) return

    setSelectedClient(project.client)
    setSelectedProject({
      id: project.id,
      name: project.name,
      logo: project.logo,
    })
    navigate(`/projects/${projectId}`)
  }

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handleSearchChange = useCallback((term) => {
    setPage(1)
    setSearch(term)
  }, [])

  const handleSortChange = useCallback((nextSort) => {
    setPage(1)
    setSort(nextSort)
  }, [])

  // Creating a project lands it on page 1 (backend default `ordering=-id`),
  // so refresh also jumps back to the first page.
  const handleRefresh = useCallback(() => {
    setPage(1)
    setRefresh(new Date().getTime())
  }, [])

  // Grid configuration
  const headers = [
    {
      title: 'Project name',
      key: 'name',
      type: 'string',
      isSortable: true,
      isSearchable: true,
      serverKey: 'name',
    },
    {
      title: 'Client',
      key: 'client',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
    },
    {
      title: 'Created',
      key: 'created_at',
      type: 'datetime',
      isSortable: true,
      isSearchable: false,
      center: true,
      serverKey: 'created_at',
    },
    {
      title: 'Model',
      key: 'solutions',
      type: 'string',
      isSortable: false,
      isSearchable: false,
      center: true,
      getValue: (rowData) => rowData.solutions.join(' '),
    },
    {
      title: 'Status',
      key: 'status',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: 'KAM',
      key: 'kam',
      type: 'string',
      isSortable: false,
      isSearchable: false,
    },
    {
      title: '',
      key: 'actions',
      type: 'none',
      isSortable: false,
      isSearchable: false,
    },
  ]

  const gridHeader = (
    <StatsContainer>
      <span css={HeaderMedium600Style}>Total Projects: {total}</span>
      <div>
        <SmallNormalTag>{active} active</SmallNormalTag>
        <SmallNormalTag>{inactive} inactive</SmallNormalTag>
      </div>
    </StatsContainer>
  )

  // Get flattened projects data for VersatileGrid
  const flattenedProjects = flattenProjectData(projects)

  return (
    <>
      {/* Page Header Section */}
      <HeaderContainer>
        <SectionTitle>Manage Projects</SectionTitle>
        <ThickSelectPicker
          placeholder="Select project"
          data={projectOptions.map((project) => ({
            label: project.name,
            value: project.id,
          }))}
          onChange={handleManage}
          style={{ width: 250 }}
        />
      </HeaderContainer>

      {/* Main Content Section */}
      <SimpleAccordion
        id={'manageProjectsPageAccordion'}
        defaultActiveKey={['1']}
      >
        <SimpleAccordion.Panel eventKey="1" noCollapse>
          {/* Project Creation Interface */}
          <ProjectForm setRefresh={handleRefresh} />

          {/* Project Data Grid - Now using flattened data */}
          <VersatileGrid
            headers={headers}
            data={flattenedProjects}
            renderRow={(rowData) => [
              <GridCellBoldText>{rowData.name}</GridCellBoldText>,
              <GridCellNormalText>{rowData.client}</GridCellNormalText>,
              <GridCellSmallFadedText>
                {new Date(rowData.created_at).toISOString().slice(0, 10)}
              </GridCellSmallFadedText>,
              <GridCellNormalText>
                {rowData.solutions.length > 0 ? (
                  rowData.solutions.map((solution, index) => (
                    <VerySmallNormalTag key={index}>
                      {solution}
                    </VerySmallNormalTag>
                  ))
                ) : (
                  <GridCellSmallFadedText>No model</GridCellSmallFadedText>
                )}
              </GridCellNormalText>,
              <GridCellNormalText>
                {rowData.status === 'active' ? (
                  <TagSuccess label={rowData.status} />
                ) : (
                  <TagWarning label={rowData.status} />
                )}
              </GridCellNormalText>,
              <GridCellSmallFadedText>{rowData.kam}</GridCellSmallFadedText>,
              <SmallStandardButton
                onClick={() => handleManage(rowData.id)}
                startIcon={<GearIcon />}
                data-testid={`manage-${rowData.name}`}
              >
                Manage
              </SmallStandardButton>,
            ]}
            maxDisplayedItems={PAGE_SIZE}
            serverSide
            serverTotal={total}
            serverPage={page}
            onPageChange={handlePageChange}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            loading={loading}
            headerInfo={gridHeader}
            loadingMessage={'Loading projects...'}
          />
        </SimpleAccordion.Panel>
      </SimpleAccordion>
    </>
  )
}

export default ProjectListPage
